<template>
  <v-app>
    <v-content>
      <v-container fluid>
        <v-layout align-center justify-center>
          <v-card style="width: 700px">
            <v-card-title primary-title>
              <div>
                <div class="headline ps-2 pa-1">{{ $t("settingsPage.serviceCommissionsTitle") }}</div>
                <div class="ps-2 grey--text">
                  {{ $t("settingsPage.commissionsHint") }}
                </div>
              </div>
            </v-card-title>

            <v-flex>
              <div class="pa-4 pt-6">
                <label style="font-size: large">{{ $t("settingsPage.service") }}</label>
                <v-select
                  outline
                  :items="serviceItems"
                  item-text="label"
                  item-value="id"
                  v-model="selectedServiceId"
                  :label="$t('settingsPage.selectService')"
                  :loading="loadingList"
                  style="margin-top: 15px"
                ></v-select>
              </div>

              <v-form
                v-if="selectedServiceId !== null"
                ref="form"
                v-model="form"
                class="pa-4 pt-0"
              >
                <label style="font-size: large">{{ $t("settingsPage.companyCommissionPercent") }}</label>
                <v-text-field
                  outline
                  v-model.number="model.company_commission_percentage"
                  :rules="[rules.required, rules.percentage]"
                  :label="$t('settingsPage.companyCommission')"
                  type="number"
                  style="margin-top: 15px"
                ></v-text-field>

                <label style="font-size: large">{{ $t("settingsPage.driverCommissionPercent") }}</label>
                <v-text-field
                  outline
                  v-model.number="model.driver_commission_percentage"
                  :rules="[rules.required, rules.percentage]"
                  :label="$t('settingsPage.driverCommission')"
                  type="number"
                  style="margin-top: 15px"
                ></v-text-field>

                <label style="font-size: large">{{ $t("settingsPage.ownerCommissionPercent") }}</label>
                <v-text-field
                  outline
                  v-model.number="model.owner_commission_percentage"
                  :rules="[rules.required, rules.percentage]"
                  :label="$t('settingsPage.ownerCommission')"
                  type="number"
                  style="margin-top: 15px"
                ></v-text-field>

                <div
                  class="pb-4"
                  :class="remainderValid ? 'green--text' : 'red--text'"
                >
                  {{ $t("settingsPage.remainderLine", { sum: remainderSum }) }}
                </div>

                <v-btn
                  :loading="loading"
                  :disabled="!remainderValid"
                  block
                  @click="save"
                  color="secondary"
                >
                  {{ $t("settingsPage.save") }}
                </v-btn>
              </v-form>
            </v-flex>
          </v-card>
        </v-layout>
      </v-container>
    </v-content>
  </v-app>
</template>

<script>
import axios from "axios";

export default {
  title() {
    return this.$t("settingsPage.serviceCommissionsTitle");
  },
  name: "ServiceCommissionsSettings",
  data: () => ({
    loading: false,
    loadingList: false,
    form: false,
    services: [],
    selectedServiceId: null,
    model: {
      company_commission_percentage: null,
      driver_commission_percentage: null,
      owner_commission_percentage: null,
    },
  }),
  computed: {
    // rules live in computed (not data) so validation messages re-resolve on locale switch
    rules() {
      return {
        required: (v) =>
          (v !== null && v !== "" && v !== undefined) || this.$t("settingsPage.ruleRequired"),
        percentage: (v) =>
          (/^\d+(\.\d+)?$/.test(String(v)) && Number(v) >= 0 && Number(v) <= 100) ||
          this.$t("settingsPage.rulePercentage"),
      };
    },
    serviceItems() {
      const isAr = this.$i18n && this.$i18n.locale === "ar";
      return this.services.map((s) => ({
        id: s.id,
        label: isAr
          ? s.name_ar || s.name_en || `#${s.id}`
          : s.name_en || s.name_ar || `#${s.id}`,
      }));
    },
    remainderSum() {
      const d = Number(this.model.driver_commission_percentage) || 0;
      const o = Number(this.model.owner_commission_percentage) || 0;
      return Math.round((d + o) * 100) / 100;
    },
    remainderValid() {
      return Math.abs(this.remainderSum - 100) < 0.001;
    },
  },
  watch: {
    selectedServiceId(id) {
      const service = this.services.find((s) => s.id === id);
      if (!service) return;
      this.model = {
        company_commission_percentage: this.toNumberOrNull(service.company_commission_percentage),
        driver_commission_percentage: this.toNumberOrNull(service.driver_commission_percentage),
        owner_commission_percentage: this.toNumberOrNull(service.owner_commission_percentage),
      };
    },
  },
  mounted() {
    this.fetchServices();
  },
  methods: {
    toNumberOrNull(v) {
      return v === null || v === undefined || v === "" ? null : Number(v);
    },
    fetchServices() {
      this.loadingList = true;
      axios
        .get("service-commissions")
        .then((response) => {
          this.services = response.data.data || [];
        })
        .catch(() => {
          window.getApp.$emit("SHOW_TOAST", this.$t("settingsPage.failedLoadServices"), "red");
        })
        .finally(() => (this.loadingList = false));
    },
    save() {
      if (!this.$refs.form.validate()) {
        window.getApp.$emit("SHOW_TOAST", this.$t("settingsPage.enterValidValues"), "red");
        return;
      }
      if (!this.remainderValid) {
        window.getApp.$emit(
          "SHOW_TOAST",
          this.$t("settingsPage.driverOwnerMustEqual100"),
          "red"
        );
        return;
      }

      this.loading = true;
      axios
        .put(`service-commissions/${this.selectedServiceId}`, {
          company_commission_percentage: Number(this.model.company_commission_percentage),
          driver_commission_percentage: Number(this.model.driver_commission_percentage),
          owner_commission_percentage: Number(this.model.owner_commission_percentage),
        })
        .then(() => {
          window.getApp.$emit("SHOW_TOAST", this.$t("settingsPage.commissionSaved"));
          // keep the local list in sync so re-selecting shows saved values
          const service = this.services.find((s) => s.id === this.selectedServiceId);
          if (service) {
            service.company_commission_percentage = Number(this.model.company_commission_percentage);
            service.driver_commission_percentage = Number(this.model.driver_commission_percentage);
            service.owner_commission_percentage = Number(this.model.owner_commission_percentage);
          }
        })
        .catch(({ response }) => {
          const msg =
            (response && response.data && response.data.message) ||
            this.$t("settingsPage.failedSaveCommission");
          window.getApp.$emit("SHOW_TOAST", msg, "red");
        })
        .finally(() => (this.loading = false));
    },
  },
};
</script>

<style scoped></style>
